/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService, PromptService } from '@dm/core';
import { DownloadService } from 'app/common/services';
import { type AVConfig, type AVServer, type OnAccessPolicy, type OnAccessTask, type OnDemandPolicy, type OnDemandTask } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AntivirusStrService, ScanPolicyTypeE } from './antivirus-str.service';

@Injectable({ providedIn: 'root' })
export class AntivirusService {
    constructor(
        private httpService: HttpService,
        private downloadService: DownloadService,
        private promptService: PromptService,
        private str: AntivirusStrService,
    ) { }

    /**
     * 获取查询防病毒配置rest
     */
    getAntivirusConfigRest(): Rest {
        const rest = this.httpService.makeUrl('antivirus.get', undefined, true);
        return rest;
    }

    /**
     * 获取查询防病毒服务器
     */
    getAntivirusServerRest(): Rest {
        const rest = this.httpService.makeUrl('antivirus.getServer', undefined, true);
        return rest;
    }

    /**
     * 获取批量查询防病毒服务器rest
     */
    getBatchServerRest(): Rest {
        const rest = this.httpService.makeUrl('antivirus.getServer', undefined, true);
        return rest;
    }

    /**
     * 获取批量查询文件系统rest
     */
    getBatchFilesystemRest(): Rest {
        const rest = this.httpService.makeUrl('fileSystem.getBatch', undefined, true);
        return rest;
    }


    /**
     * 获取批量查询扫描任务rest
     */
    getBatchAvTaskRest(): Rest {
        const rest = this.httpService.makeUrl('antivirus.getBatchAvTask', undefined, true);
        return rest;
    }

    /**
     * 获取批量查询后台扫描任务rest
     */
    getBatchOnDemandTaskRest(): Rest {
        const rest = this.httpService.makeUrl('antivirus.getBatchOnDemandTask', undefined, true);
        return rest;
    }

    /**
     * 获取批量查询防病毒按需扫描策略rest
     */
    getBatchOnDemandPolicyRest(vstoreId?: string): Rest {
        const rest = this.httpService.makeUrl('antivirus.getBatchOnDemandPolicy', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取批量查询防病毒实时扫描策略rest
     */
    getBatchOnAccessPolicyRest(vstoreId?: string): Rest {
        const rest = this.httpService.makeUrl('antivirus.getBatchOnAccessPolicy', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 分类查询扫描策略数据
     *
     * @param {ScanPolicyTypeE} type
     * @param {string} vstoreId
     * @returns Observable<HttpResponse>
     */
    getBatchPolicy(type: ScanPolicyTypeE, vstoreId: string): Observable<HttpResponse> {
        const rest = type === ScanPolicyTypeE.ONACCESS ?
            this.getBatchOnAccessPolicyRest(vstoreId) :
            this.getBatchOnDemandPolicyRest(vstoreId);
        return this.httpService.get(rest);
    }

    /**
     * 查询防病毒配置
     */
    getAntivirusConfig(options?: BaseRequestOptionsArgs): Observable<AVConfig> {
        const rest = this.httpService.makeUrl('antivirus.get', undefined, true);
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询防病毒服务器
     */
    getAntivirusServer(options?: BaseRequestOptionsArgs): Observable<AVServer[]> {
        const rest = this.httpService.makeUrl('antivirus.getServer', undefined, true);
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询单个防病毒后台任务
     */
    getAvTask(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.getAvTask', undefined, true)
            .setParams({ fileSystemID: id });

        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 查询单个防病毒实时策略
     */
    getOnAccessPolicy(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.getOnAccessPolicy', undefined, true).format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 查询单个防病毒后台策略
     */
    getOnDemandPolicy(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.getOnDemandPolicy', undefined, true).format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 查询单个防病毒后台任务
     */
    getOnDemandTask(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.getOnDemandTask', undefined, true)
            .setParams({ fileSystemID: id });
        return this.httpService.get(rest, options);
    }

    /**
     * 修改防病毒服务状态
     */
    modifyServiceStatus(status: boolean, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(status ? 'antivirus.enableService' : 'antivirus.disableService', undefined, true);
        const params: AVConfig = {
            enableScan: Number(status),
        };
        return this.httpService.put(rest, params, options);
    }

    /**
     * 测试防病毒服务器
     */
    testAntivirusServer(params: AVServer, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.testServer', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 添加防病毒服务器
     */
    addAntivirusServer(params: AVServer, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.addServer', undefined, true);
        return this.httpService.post(rest, params, options);
    }

    /**
     * 移除防病毒服务器
     */
    removeAntivirusServer(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.removeServer', undefined, true).format(id);
        return this.httpService.delete(rest, options);
    }

    /**
     * 修改预共享密钥
     */
    modifyPresharedKey(params: AVConfig, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.modifyPresharedKey', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 下载AV代理插件
     */
    downloadAvPlugin(): void {
        this.promptService.info({
            id: 'prompt-antivirus-downloadAvPlugin',
            title: this.str.i18n.downloadPluginTitle,
            content: this.str.i18n.downloadPluginHighRisk,
            onOk: () => {
                this.promptService.close();
                const rest = this.httpService.makeUrl('antivirus.getPlugin');
                this.downloadService.downloadFile(rest);
            },
        });
    }

    /**
     * 创建后台扫描任务
     */
    createOnDemandTask(params: OnDemandTask, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.createOndemandTask', undefined, true);
        return this.httpService.post(rest, params, options);
    }

    /**
     * 删除后台扫描任务
     */
    deleteOnDemandTask(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.deleteOndemandTask', undefined, true)
            .setParams({ fileSystemID: id });
        return this.httpService.delete(rest, options);
    }

    /**
     * 开始后台扫描任务
     */
    modifyOnDemandTask(params: OnDemandTask, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.modifyOndemandTask', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 创建实时扫描任务
     */
    createOnAccessTask(params: OnAccessTask, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.createOnaccessTask', undefined, true);
        return this.httpService.post(rest, params, options);
    }

    /**
     * 删除实时扫描任务
     */
    deleteOnAccessTask(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.deleteOnaccessTask', undefined, true)
            .setParams({ fileSystemID: id });
        return this.httpService.delete(rest, options);
    }

    /**
     * 开始实时扫描任务
     */
    modifyOnAccessTask(params: OnAccessTask, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.modifyOnaccessTask', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 创建后台扫描策略
     */
    createOnDemandPolicy(params: OnDemandPolicy, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.createOndemandPolicy', undefined, true);
        if (params.vstoreId) {
            rest.setParams({ vstoreId: params.vstoreId });
        }
        return this.httpService.post(rest, params, options);
    }

    /**
     * 修改后台扫描策略
     */
    modifyOnDemandPolicy(params: OnDemandPolicy, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.modifyOndemandPolicy', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 删除后台扫描策略
     */
    deleteOnDemandPolicy(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.deleteOndemandPolicy', undefined, true).format(id);
        return this.httpService.delete(rest, options);
    }

    /**
     * 创建实时扫描策略
     */
    createOnAccessPolicy(params: OnAccessPolicy, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.createOnAccessPolicy', undefined, true);
        if (params.vstoreId) {
            rest.setParams({ vstoreId: params.vstoreId });
        }
        return this.httpService.post(rest, params, options);
    }

    /**
     * 修改实时扫描策略
     */
    modifyOnAccessPolicy(params: OnAccessPolicy, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.modifyOnAccessPolicy', undefined, true);
        return this.httpService.put(rest, params, options);
    }

    /**
     * 删除实时扫描策略
     */
    deleteOnAccessPolicy(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('antivirus.deleteOnAccessPolicy', undefined, true).format(id);
        return this.httpService.delete(rest, options);
    }
}
